import {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {ChevronLeft, Loader2, Lock, MicOff, MonitorUp, Phone, RefreshCw, ShieldAlert, UserPlus, Users, Video, Wifi} from 'lucide-react';
import {useMaybeRoomContext} from '@livekit/components-react';
import type {GroupCallState, RingTargetState} from '@phonehq/rtc-core';
import {matrixService} from '../../../services/matrixService';
import {groupCallMessages as gm} from '../../groupCalls/messages';
import {createRoomMemberDirectory, useCallMember} from '../../groupCalls/components/callContext';
import {errorMessage} from '../../groupCalls/components/noticeMessages';
import {useRoomCallSummary} from '../../groupCalls/hooks/useGroupCall';
import {BubbleActionButton, BubbleChip, BubbleControl, BubbleEndButton, BubbleIconButton, ExpandButton, StatusNote} from '../components/controls';
import {MemberAvatar} from '../components/MemberAvatar';
import type {GroupCallLeg} from '../model/types';
import {useElapsed} from '../hooks/useElapsed';
import {groupActions} from './groupActions';
import {GroupMoreMenu} from './GroupMoreMenu';
import {GroupTile, SelfView} from './GroupTile';
import {useGroupTracks, useStableTiles} from './useGroupTracks';
import type {ControlSpec, GroupControls} from './useGroupControls';
import {groupSurfaceMessages as m} from './groupMessages';
import {AddPeoplePanel} from './AddPeoplePanel';
import {InviteeRow, InviteesSection} from './InviteesSection';
import {InviteBanner} from './GroupInvite';
import {AloneCountdown} from './AloneCountdown';
import type {AloneAutoLeave} from './useAloneAutoLeave';
import type {CallSwitch, IncomingGroupCall} from '../../groupCalls/services/groupCallOrchestrator';
import type {CallPerson} from './useCallPeople';

const STACK_SIZE = 3;
const BUBBLE_TILES = 4;
const BUBBLE_WAITING_ROWS = 3;
const NO_WAITING: readonly RingTargetState[] = [];

const RoomGlyph = () => (
    <span aria-hidden="true" className="inline-flex h-16 w-16 items-center justify-center rounded-[20px] bg-[var(--phq-call-brand-tint)] text-[var(--phq-call-brand-ink)]">
        <Users size={28}/>
    </span>
);

export const GroupChip = ({leg}: {leg: GroupCallLeg}) => {
    const intl = useIntl();
    const timer = useElapsed(leg.phase === 'active' ? leg.sessionStartedAt ?? leg.connectedAt : undefined);
    if (leg.encryption === 'warning') {
        return <BubbleChip icon={<ShieldAlert size={13} aria-hidden="true"/>} testId="group-call-encryption-badge" status="warning">{intl.formatMessage(m.chipWarning)}</BubbleChip>;
    }
    if (leg.encryption === 'pending') {
        return <BubbleChip icon={<Lock size={13} aria-hidden="true"/>} testId="group-call-encryption-badge" status="pending">{intl.formatMessage(m.chipSecuring)}</BubbleChip>;
    }
    return (
        <BubbleChip icon={<Lock size={13} aria-hidden="true"/>} testId="group-call-encryption-badge" status="ok">
            {timer ? intl.formatMessage(m.chipTeamCallTimer, {timer}) : intl.formatMessage(m.chipTeamCall)}
        </BubbleChip>
    );
};

const InviteStack = ({roomId, userIds}: {roomId: string; userIds: readonly string[]}) => {
    const directory = useMemo(() => createRoomMemberDirectory(matrixService.getRoom(roomId)), [roomId]);
    return (
        <span className="flex items-center pl-3" aria-hidden="true">
            {userIds.slice(0, STACK_SIZE).map(userId => {
                const profile = directory(userId);
                return (
                    <span key={userId} className="-ml-3 rounded-[15px] border-[3px] border-[#1E1A4D]">
                        <MemberAvatar userId={userId} name={profile.name} mxcAvatarUrl={profile.mxcAvatarUrl} size={40}/>
                    </span>
                );
            })}
        </span>
    );
};

export const GroupIncomingBubble = ({leg, videoAvailable}: {leg: GroupCallLeg; videoAvailable: boolean}) => {
    const intl = useIntl();
    const summary = useRoomCallSummary(leg.roomId);
    const inCall = summary?.userIds ?? [];
    const room = leg.roomName ?? intl.formatMessage(gm.historyUnknownRoom);
    const video = leg.intent === 'video' && videoAvailable;
    return (
        <div className="phq-call-swap flex flex-col gap-3" data-testid="incoming-group-call">
            <div className="flex min-h-8 items-center">
                <BubbleChip icon={<Lock size={13} aria-hidden="true"/>}>{intl.formatMessage(m.chipTeamCall)}</BubbleChip>
            </div>
            <div className="flex flex-col items-center gap-2 text-center">
                {inCall.length > 0 ? <InviteStack roomId={leg.roomId} userIds={inCall}/> : <span className="phq-call-ring rounded-[20px]"><RoomGlyph/></span>}
                <p className="text-lg font-semibold leading-[1.25]">{intl.formatMessage(m.incomingTitle, {name: leg.identity.name ?? room})}</p>
                <p className="text-sm text-[var(--phq-call-ink-muted)]">
                    {inCall.length > 0 ? intl.formatMessage(m.incomingSubline, {room, count: inCall.length}) : intl.formatMessage(m.incomingRoomOnly, {room})}
                </p>
            </div>
            <div className="flex gap-2">
                <BubbleActionButton
                    tone="danger"
                    icon={<Phone size={18} className="rotate-[135deg]" aria-hidden="true"/>}
                    label={intl.formatMessage(m.decline)}
                    testId="incoming-group-call-decline"
                    onClick={groupActions.decline}
                />
                <BubbleActionButton
                    tone="accept"
                    icon={video ? <Video size={18} aria-hidden="true"/> : <Phone size={18} aria-hidden="true"/>}
                    label={intl.formatMessage(m.join)}
                    ariaLabel={intl.formatMessage(video ? gm.incomingJoinVideo : gm.incomingJoinAudio)}
                    testId={video ? 'incoming-group-call-join-video' : 'incoming-group-call-join-audio'}
                    onClick={() => groupActions.answer(video ? 'video' : 'audio')}
                />
            </div>
            {video && (
                <button
                    type="button"
                    data-testid="incoming-group-call-join-audio"
                    onClick={() => groupActions.answer('audio')}
                    className="phq-call-press inline-flex h-10 w-full items-center justify-center rounded-2xl bg-[var(--phq-call-surface-muted)] text-[15px] font-semibold"
                >
                    {intl.formatMessage(m.joinAudioOnly)}
                </button>
            )}
        </div>
    );
};

export const GroupJoiningBubble = ({leg, leaving}: {leg: GroupCallLeg; leaving: boolean}) => {
    const intl = useIntl();
    return (
        <div className="phq-call-swap flex flex-col gap-3">
            <div className="flex min-h-8 items-center">
                <GroupChip leg={leg}/>
            </div>
            <div className="flex flex-col items-center gap-2 text-center">
                <span className="phq-call-ring rounded-[20px]"><RoomGlyph/></span>
                <p className="text-lg font-semibold leading-tight">{leg.roomName}</p>
                <p className="inline-flex items-center gap-2 text-sm font-medium text-[var(--phq-call-ink-muted)]">
                    <Loader2 size={14} className="animate-spin" aria-hidden="true"/>
                    {intl.formatMessage(leaving ? m.leaving : m.joining)}
                </p>
            </div>
            {!leaving && <BubbleEndButton label={intl.formatMessage(m.cancel)} onClick={groupActions.leave}/>}
        </div>
    );
};

const BubbleTiles = ({waiting, autoLeave}: {waiting: readonly RingTargetState[]; autoLeave?: AloneAutoLeave}) => {
    const intl = useIntl();
    const {cameras, speakerIdentity, speaking} = useGroupTracks();
    const others = useMemo(() => cameras.filter(tile => !tile.view.isLocal), [cameras]);
    const remote = useStableTiles(others, speakerIdentity, BUBBLE_TILES);
    const local = cameras.find(tile => tile.view.isLocal && tile.view.cameraEnabled);
    if (remote.length === 0 && waiting.length > 0) {
        return (
            <div className="flex flex-col rounded-xl bg-[var(--phq-call-surface)] pb-1 pt-3" data-testid="group-call-waiting">
                <div className="px-4 pb-1">
                    <p className="text-[15px] font-semibold">{intl.formatMessage(autoLeave ? m.noAnswerTitle : m.waitingTitle)}</p>
                    {autoLeave
                        ? <AloneCountdown autoLeave={autoLeave} className="mt-1"/>
                        : <p className="text-[13px] text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.waitingInvited, {count: waiting.length})}</p>}
                </div>
                <ul className="flex flex-col">
                    {waiting.slice(0, BUBBLE_WAITING_ROWS).map(target => <InviteeRow key={target.matrixUserId} target={target}/>)}
                </ul>
            </div>
        );
    }
    if (remote.length === 0) {
        return (
            <div className="flex flex-col items-center gap-1 rounded-xl bg-[var(--phq-call-surface)] px-4 py-5 text-center" data-testid="group-call-alone">
                {local && <SelfView trackRef={local.trackRef} speaking={speaking.has(local.view.identity)} className="mb-2 aspect-video w-[160px]"/>}
                <p className="text-[15px] font-semibold">{intl.formatMessage(m.aloneTitle)}</p>
                <p className="text-[13px] text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.aloneBody)}</p>
            </div>
        );
    }
    return (
        <div className="relative">
            <div className={`grid gap-1.5 ${remote.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {remote.map(({trackRef, view}) => (
                    <GroupTile key={view.identity} trackRef={trackRef} view={view} variant="bubble" speaking={speaking.has(view.identity)}/>
                ))}
            </div>
            {local && <SelfView trackRef={local.trackRef} speaking={speaking.has(local.view.identity)} className="absolute bottom-1.5 right-1.5 h-12 w-[72px]"/>}
        </div>
    );
};

const ShareRow = ({label, action, tone, onAction}: {label: string; action: string; tone: 'danger' | 'neutral'; onAction: () => void}) => (
    <div className="phq-call-swap flex items-center gap-2 rounded-xl bg-[var(--phq-call-surface)] py-1.5 pl-3 pr-1.5 text-[13px] font-medium">
        <MonitorUp size={16} className="shrink-0 text-[var(--phq-call-brand-focus)]" aria-hidden="true"/>
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <button
            type="button"
            onClick={onAction}
            className={`phq-call-press inline-flex h-7 shrink-0 items-center rounded-lg px-2.5 text-xs font-semibold ${tone === 'danger' ? 'bg-[var(--phq-call-danger)] text-white' : 'bg-[var(--phq-call-surface-muted)]'}`}
        >
            {action}
        </button>
    </div>
);

interface GroupActiveBubbleProps {
    readonly leg: GroupCallLeg;
    readonly waiting?: readonly RingTargetState[];
    readonly autoLeave?: AloneAutoLeave;
    readonly invite?: IncomingGroupCall;
    readonly onDeclineInvite?: () => void;
    readonly onAcceptInvite?: () => void;
    readonly sharer?: string;
    readonly controls: GroupControls;
    readonly handRaised: boolean;
    readonly onPopOut?: () => void;
    readonly onOpenDevices: () => void;
}

export const GroupActiveBubble = ({leg, waiting = NO_WAITING, autoLeave, invite, onDeclineInvite, onAcceptInvite, sharer, controls, handRaised, onPopOut, onOpenDevices}: GroupActiveBubbleProps) => {
    const intl = useIntl();
    const room = useMaybeRoomContext();
    const reconnecting = leg.network === 'reconnecting';
    const items = [controls.microphone, controls.camera, controls.share, controls.people].filter((item): item is ControlSpec => item !== undefined);
    return (
        <div className="phq-call-swap flex flex-col gap-3" data-testid="group-call-in-call">
            <div className="flex min-h-8 items-center justify-between gap-2">
                <GroupChip leg={leg}/>
                <ExpandButton label={intl.formatMessage(m.expandCall)} onClick={groupActions.expand}/>
            </div>
            {invite && onDeclineInvite && onAcceptInvite && (
                <InviteBanner invite={invite} variant="bubble" onDecline={onDeclineInvite} onLeaveAndJoin={onAcceptInvite}/>
            )}
            {room ? <BubbleTiles waiting={waiting} autoLeave={autoLeave}/> : null}
            {leg.local.screenSharing && (
                <ShareRow label={intl.formatMessage(m.sharingYou)} action={intl.formatMessage(m.stopShort)} tone="danger" onAction={groupActions.stopScreenShare}/>
            )}
            {!leg.local.screenSharing && sharer && (
                <ShareRow label={intl.formatMessage(gm.tileScreen, {name: sharer})} action={intl.formatMessage(m.watch)} tone="neutral" onAction={groupActions.expand}/>
            )}
            {reconnecting && <StatusNote tone="reconnecting" icon={<RefreshCw size={16} className="animate-spin" aria-hidden="true"/>}>{intl.formatMessage(m.noteReconnecting)}</StatusNote>}
            {!reconnecting && leg.network === 'weak' && <StatusNote tone="weak" icon={<Wifi size={16} aria-hidden="true"/>}>{intl.formatMessage(m.noteWeak)}</StatusNote>}
            {!reconnecting && leg.muted && !leg.local.deviceIssues?.microphone && (
                <StatusNote tone="muted" icon={<MicOff size={16} aria-hidden="true"/>}>{intl.formatMessage(m.noteMuted)}</StatusNote>
            )}
            <div className={`flex items-center justify-between ${reconnecting ? 'opacity-40' : ''}`}>
                {items.map(item => (
                    <BubbleControl
                        key={item.id}
                        icon={item.icon}
                        label={item.label}
                        tone={item.tone}
                        alert={item.alert}
                        badge={item.badge}
                        pressed={item.pressed}
                        hasPopup={item.hasPopup}
                        disabled={item.disabled || reconnecting}
                        testId={item.testId}
                        onClick={item.onClick}
                    />
                ))}
                <GroupMoreMenu variant="bubble" handRaised={handRaised} disabled={reconnecting} onPopOut={onPopOut} onOpenDevices={onOpenDevices}/>
            </div>
            <BubbleEndButton label={intl.formatMessage(gm.controlLeave)} testId="group-call-leave" onClick={groupActions.leave}/>
        </div>
    );
};

const PeopleRow = ({userId, isLocal, reconnecting, muted}: {userId: string; isLocal: boolean; reconnecting: boolean; muted: boolean}) => {
    const intl = useIntl();
    const profile = useCallMember(userId);
    return (
        <li className="flex min-h-12 items-center gap-3" data-testid="group-call-participant-row">
            <MemberAvatar userId={userId} name={profile.name} mxcAvatarUrl={profile.mxcAvatarUrl} size={32}/>
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{isLocal ? intl.formatMessage(m.peopleYou, {name: profile.name}) : profile.name}</p>
                <p className="text-[13px] text-[var(--phq-call-ink-muted)]">
                    {intl.formatMessage(reconnecting ? m.peopleStatusReconnecting : m.peopleStatusInCall)}
                </p>
            </div>
            {muted && <MicOff size={16} className="text-[var(--phq-call-dnd)]" aria-label={intl.formatMessage(gm.tileMuted)}/>}
        </li>
    );
};

interface GroupPeopleBubbleProps {
    readonly people: readonly CallPerson[];
    readonly invitees: readonly RingTargetState[];
    readonly onBack: () => void;
    readonly onAddPeople?: () => void;
}

export const GroupPeopleBubble = ({people, invitees, onBack, onAddPeople}: GroupPeopleBubbleProps) => {
    const intl = useIntl();
    return (
        <div className="phq-call-swap flex flex-col gap-3" data-testid="group-call-participants-panel">
            <div className="flex items-center gap-3">
                <BubbleIconButton label={intl.formatMessage(m.peopleClose)} icon={<ChevronLeft size={16} aria-hidden="true"/>} onClick={onBack}/>
                <h2 className="text-[15px] font-semibold">{intl.formatMessage(gm.participantsTitle, {count: people.length})}</h2>
            </div>
            <div className="-mx-4 max-h-[320px] overflow-y-auto">
                <ul className="flex flex-col px-4">
                    {people.map(person => (
                        <PeopleRow key={person.userId} userId={person.userId} isLocal={person.isLocal} reconnecting={person.reconnecting} muted={person.muted}/>
                    ))}
                </ul>
                <InviteesSection invitees={invitees}/>
            </div>
            {onAddPeople && (
                <BubbleActionButton tone="brand" icon={<UserPlus size={16} aria-hidden="true"/>} label={intl.formatMessage(m.addPeople)} testId="group-call-add-people-open" onClick={onAddPeople}/>
            )}
            <BubbleEndButton label={intl.formatMessage(gm.controlLeave)} onClick={groupActions.leave}/>
        </div>
    );
};

export const GroupAddPeopleBubble = ({roomId, people, invitees, onBack}: {roomId: string; people: readonly CallPerson[]; invitees: readonly RingTargetState[]; onBack: () => void}) => {
    const inCall = useMemo(() => new Set(people.map(person => person.userId)), [people]);
    return (
        <div className="phq-call-swap flex flex-col">
            <AddPeoplePanel roomId={roomId} peopleInCall={inCall} invitees={invitees} onBack={onBack} compact/>
        </div>
    );
};

export const GroupFailedBubble = ({state, switchFailed}: {state: GroupCallState; switchFailed?: CallSwitch}) => {
    const intl = useIntl();
    if (switchFailed) {
        return (
            <div className="phq-call-swap flex flex-col gap-3" role="alert" data-testid="group-call-failed">
                <div className="flex flex-col items-center gap-2 py-2 text-center">
                    <p className="text-lg font-semibold leading-tight">{intl.formatMessage(m.switchFailedTitle, {room: switchFailed.to})}</p>
                    <p className="text-sm text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.switchFailedBody, {from: switchFailed.from})}</p>
                </div>
                <div className="flex gap-2">
                    <BubbleActionButton tone="brand" label={intl.formatMessage(m.tryAgain)} testId="group-call-switch-retry" onClick={groupActions.retrySwitch}/>
                    <BubbleActionButton tone="neutral" label={intl.formatMessage(m.dismiss)} testId="group-call-failed-close" onClick={groupActions.dismissSwitchFailure}/>
                </div>
            </div>
        );
    }
    return <GroupCallFailure state={state}/>;
};

const GroupCallFailure = ({state}: {state: GroupCallState}) => {
    const intl = useIntl();
    const roomId = state.roomId;
    const network = state.error?.code === 'network';
    const full = state.error?.code === 'call_full';
    const canRetry = !!roomId && state.error?.code !== 'not_encrypted';
    const title = network ? m.disconnectedTitle : full ? m.fullTitle : gm.failedTitle;
    return (
        <div className="phq-call-swap flex flex-col gap-3" role="alert" data-testid="group-call-failed">
            <div className="flex flex-col items-center gap-2 py-2 text-center">
                <p className="text-lg font-semibold leading-tight">{intl.formatMessage(title)}</p>
                <p className="text-sm text-[var(--phq-call-ink-muted)]">{intl.formatMessage(network ? m.disconnectedBody : full ? m.fullBody : errorMessage(state.error?.code))}</p>
            </div>
            <div className="flex gap-2">
                {canRetry && roomId && (
                    <BubbleActionButton tone="brand" label={intl.formatMessage(network ? m.rejoin : gm.failedRetry)} onClick={() => groupActions.rejoin(roomId, state.intent)}/>
                )}
                <BubbleActionButton tone="neutral" label={intl.formatMessage(gm.failedClose)} testId="group-call-failed-close" onClick={groupActions.dismiss}/>
            </div>
        </div>
    );
};
